import { c as _c } from "react/compiler-runtime";
import type { Address, BigIntish, ChainId } from '@morpho-org/blue-sdk';
import { InterfaceChainUtils, type Point, type Rewards, toBigInt } from '@morpho-org/dapp-core';
import { orderBy } from 'lodash-es';
import { useMemo } from 'react';
import type { Warning } from '@/components/common/WarningIcon';
import type { VaultV2Exposure } from '@/lib/procedures/vaultV2Exposure.contract';
import { sortPendingTimelocks as sortV1PendingTimelocks } from '@/components/VaultPage/VaultV1/VaultPendingTimelock/utils';
import { getMarketUrl, getVaultUrl } from '@/helpers/routing';
import { getMarketWarningMessage, getVaultWarningMessage } from '@/helpers/warningMessages';
import { apiWarningLevelToEnum } from '@/helpers/warnings';
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider';
import type { UnderlyingVaultPendingTimelock, UnderlyingVaultPendingTimelockWithCaps, VaultV2Caps, VaultV2PendingTimelock } from '../../../types/pendingTimelocks';
import { type Asset, toAsset, toCollateralAsset } from '../../../VaultV2HeroPendingActions/toPendingTimelocks';
import { getMarketV1Apy, getMetaMorphoVaultApy, getWeightedMarketsApy } from '../utils/apyComputations';
import { getAdapterCapPendingTimelocks, getAdapterPendingTimelocks, getCollateralCapPendingTimelocks, getMarketCapPendingTimelocks } from '../utils/pendingTimelockFilters';

/** What the exposure section renders: the exposure plus the vault's pending timelocks. */
export interface VaultV2ExposureSectionData {
  chainId: ChainId;
  exposure: VaultV2Exposure;
  /** The vault's asset; null when Core cannot label it. */
  asset: Asset | null;
  totalAssets: string;
  idleAssets: string;
  pendingTimelocks: VaultV2PendingTimelock[];
  underlyingVaultItems: UnderlyingVaultPendingTimelockWithCaps[];
  caps: VaultV2Caps;
}
type ExposureAdapter = VaultV2Exposure['adapters'][number];
type ExposureMarket = VaultV2Exposure['markets'][number];
const VAULT_V1_KIND = 'morpho_vault_v1';
const isMarketAdapter = (adapter: ExposureAdapter) => adapter.kind.startsWith('morpho_market_v1');
const adapterCleanName = (kind: string): string => {
  if (kind === VAULT_V1_KIND) return 'Vault V1';
  if (kind.startsWith('morpho_market_v1')) return 'Variable Rate Markets';
  return 'Vault V2';
};
const WARNING_LEVELS = ['green', 'yellow', 'red'] as const;
type WarningLevel = (typeof WARNING_LEVELS)[number];
const isWarningLevel = (level: string): level is WarningLevel => (WARNING_LEVELS as readonly string[]).includes(level);
const toCap = (value: string | null) => value === null ? undefined : BigInt(value);
function toMarketWarnings(market: ExposureMarket, loanAsset: Asset | null): Warning[] {
  return market.warnings.flatMap(warning => {
    const {
      level
    } = warning;
    if (!isWarningLevel(level)) return [];
    const message = getMarketWarningMessage({
      type: warning.type,
      level,
      metadata: {
        content: warning.content,
        badDebtAssets: warning.badDebtAssets,
        badDebtUsd: warning.badDebtUsd
      }
    }, loanAsset ?? undefined);
    return message ? [{
      level: apiWarningLevelToEnum(level),
      message
    }] : [];
  });
}

/**
 * Whether Core failed to label a market the vault allocates to directly. Its unknown label reads
 * like an idle market (no collateral), which would drop its APY from the weighted averages.
 */
export function hasUnlabelledMarket(exposure: VaultV2Exposure): boolean {
  return exposure.markets.some(market => market.market.loanToken === null);
}

/** The market cell's label: its tokens, LLTV and warnings. */
export function toExposureMarketLabel(chainId: ChainId, market: ExposureMarket) {
  const loanAsset = market.market.loanToken && toAsset(chainId, market.market.loanToken);
  return {
    marketId: market.market.marketId,
    // A null collateral means an idle market; an unlabelled token keeps a placeholder asset.
    collateralAsset: market.market.collateralToken && toCollateralAsset(chainId, market.market.collateralToken),
    loanAsset,
    lltv: market.market.lltv ?? 0n,
    warnings: toMarketWarnings(market, loanAsset)
  };
}
function toVaultWarnings(warnings: NonNullable<ExposureAdapter['vaultV1']>['warnings']): Warning[] {
  return warnings.flatMap(warning => {
    const {
      level
    } = warning;
    if (!isWarningLevel(level)) return [];
    const message = getVaultWarningMessage({
      type: warning.type,
      level,
      metadata: {
        content: warning.content,
        history: warning.wasListed ? [{
          action: 'added',
          timestamp: 0
        }] : []
      }
    });
    return message ? [{
      level: apiWarningLevelToEnum(level),
      message
    }] : [];
  });
}
export type VaultV2ExposureTab = 'VaultsAndMarkets' | 'Collateral' | 'Adapters';
export type VaultV2ExposureTableRow = VaultV2ExposureTableRowItem | {
  type: 'category';
  label: string;
};
export interface VaultV2ExposureTableRowItem {
  type?: 'item';
  item: {
    type: 'VaultV1';
    vault: {
      name: string;
      address: Address;
      assetLogoURI: string | null;
      warnings: Warning[];
    };
  } | {
    type: 'MarketV1';
    market: {
      marketId: string;
      collateralAsset: Asset | null;
      loanAsset: Asset | null;
      lltv: BigIntish;
      warnings: Warning[];
    };
  } | {
    type: 'Token';
    token: Asset | null;
  } | {
    type: 'Adapter';
    name: string;
  };
  allocation: bigint;
  underlyingLiquidity?: bigint;
  utilization?: number;
  absoluteCap?: bigint;
  relativeCap?: bigint;
  totalAssets: bigint;
  isLiquidityAdapter?: boolean;
  isIdle?: boolean;
  pendingTimelocks?: VaultV2PendingTimelock[];
  underlyingVaultPendingTimelocks?: UnderlyingVaultPendingTimelock[];
  underlyingVaultMarketSupplyCaps?: Record<string, BigIntish>;
  caps?: VaultV2Caps;
  apy?: {
    native: bigint;
    net: bigint;
    performanceFee?: bigint;
    rewards: Rewards[];
    points: Point[];
    variant: 'market' | 'vault';
  };
  href?: string;
}
export const useVaultV2ExposureData = t0 => {
  const $ = _c(18);
  const {
    data,
    tab
  } = t0;
  const {
    showPoints
  } = useFeatureFlags();
  let t1;
  bb0: {
    let t2;
    if ($[0] !== data?.exposure.adapters) {
      t2 = data?.exposure.adapters ?? [];
      $[0] = data?.exposure.adapters;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    const adapters = t2;
    let t3;
    if ($[2] !== adapters) {
      t3 = adapters.length > 0 && adapters.every(isMarketAdapter);
      $[2] = adapters;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    const hasOnlyMarketV1Adapters = t3;
    let t4;
    if ($[4] !== adapters) {
      t4 = adapters.some(isMarketAdapter);
      $[4] = adapters;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    const hasMarketAllocation = t4;
    if (!data) {
      let t5;
      if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
        t5 = [];
        $[6] = t5;
      } else {
        t5 = $[6];
      }
      let t6;
      if ($[7] !== hasMarketAllocation || $[8] !== hasOnlyMarketV1Adapters) {
        t6 = {
          rows: t5,
          hasMarketAllocation,
          hasOnlyMarketV1Adapters
        };
        $[7] = hasMarketAllocation;
        $[8] = hasOnlyMarketV1Adapters;
        $[9] = t6;
      } else {
        t6 = $[9];
      }
      t1 = t6;
      break bb0;
    }
    let t5;
    if ($[10] !== data || $[11] !== showPoints || $[12] !== tab) {
      t5 = buildRows({
        data,
        tab,
        showPoints
      });
      $[10] = data;
      $[11] = showPoints;
      $[12] = tab;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    const rows = t5;
    let t6;
    if ($[14] !== hasMarketAllocation || $[15] !== hasOnlyMarketV1Adapters || $[16] !== rows) {
      t6 = {
        rows,
        hasMarketAllocation,
        hasOnlyMarketV1Adapters
      };
      $[14] = hasMarketAllocation;
      $[15] = hasOnlyMarketV1Adapters;
      $[16] = rows;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    t1 = t6;
  }
  return t1;
};
function buildRows({
  data,
  tab,
  showPoints
}: {
  data: VaultV2ExposureSectionData;
  tab: VaultV2ExposureTab;
  showPoints: boolean;
}): VaultV2ExposureTableRow[] {
  const {
    chainId,
    exposure,
    pendingTimelocks,
    caps
  } = data;
  const totalAssets = BigInt(data.totalAssets);
  const idleAllocation = BigInt(data.idleAssets);
  const marketRows = exposure.markets.map(market => ({
    market,
    label: toExposureMarketLabel(chainId, market),
    allocation: BigInt(market.allocatedAssets),
    apy: getMarketV1Apy({
      chainId,
      market,
      showPoints
    })
  }));
  switch (tab) {
    case 'VaultsAndMarkets':
      {
        const vaultV1Rows = orderBy(exposure.adapters.flatMap(adapter => {
          const {
            vaultV1
          } = adapter;
          if (adapter.kind !== VAULT_V1_KIND || !vaultV1) return [];
          const vaultItems = data.underlyingVaultItems.filter(item => item.vault.address.toLowerCase() === vaultV1.address.toLowerCase());
          const underlyingVaultPendingTimelocks: UnderlyingVaultPendingTimelock[] = vaultItems.map(item => item.pendingTimelock);
          const name = vaultV1.name ?? '';
          return [{
            item: {
              type: 'VaultV1' as const,
              vault: {
                name,
                address: vaultV1.address,
                assetLogoURI: data.asset?.logoURI ?? null,
                warnings: toVaultWarnings(vaultV1.warnings)
              }
            },
            allocation: BigInt(adapter.assets),
            underlyingLiquidity: toBigInt(adapter.underlyingLiquidity ?? undefined),
            absoluteCap: toCap(adapter.absoluteCap),
            relativeCap: toCap(adapter.relativeCap),
            totalAssets,
            isLiquidityAdapter: adapter.isLiquidityAdapter,
            pendingTimelocks: getAdapterPendingTimelocks(pendingTimelocks, adapter.address),
            underlyingVaultPendingTimelocks: sortV1PendingTimelocks(underlyingVaultPendingTimelocks),
            // Only withdraw-queue markets carry a current cap, so a pending addition reads as "Add Market".
            underlyingVaultMarketSupplyCaps: Object.fromEntries(vaultItems.flatMap(item => Object.entries(item.marketSupplyCaps))),
            caps,
            apy: getMetaMorphoVaultApy({
              chainId,
              vault: vaultV1,
              showPoints
            }),
            href: getVaultUrl({
              identifier: InterfaceChainUtils.getIdentifierById(chainId),
              vaultAddress: vaultV1.address,
              vaultName: name
            })
          }];
        }), row => row.allocation, 'desc');
        const marketV1Rows = orderBy(marketRows.map(({
          market,
          label,
          allocation,
          apy
        }) => ({
          item: {
            type: 'MarketV1' as const,
            market: label
          },
          allocation,
          underlyingLiquidity: BigInt(market.liquidityAssets),
          utilization: market.utilization,
          absoluteCap: toCap(market.absoluteCap),
          relativeCap: toCap(market.relativeCap),
          totalAssets,
          isLiquidityAdapter: market.market.marketId === exposure.liquidityMarketId,
          pendingTimelocks: getMarketCapPendingTimelocks(pendingTimelocks, market.adapterAddress, market.market.marketId),
          caps,
          apy,
          href: getMarketUrl({
            identifier: InterfaceChainUtils.getIdentifierById(chainId),
            marketId: market.market.marketId,
            collateralSymbol: label.collateralAsset?.symbol ?? '',
            loanSymbol: label.loanAsset?.symbol ?? ''
          })
        })), row => row.allocation, 'desc');
        const result: VaultV2ExposureTableRow[] = [];
        if (vaultV1Rows.length > 0) {
          result.push({
            type: 'category',
            label: 'Vaults V1'
          }, ...vaultV1Rows);
        }
        if (marketV1Rows.length > 0) {
          result.push({
            type: 'category',
            label: 'Variable Rate Markets'
          }, ...marketV1Rows);
        }
        result.push({
          type: 'category',
          label: 'Idle Liquidity'
        }, {
          item: {
            type: 'Token' as const,
            token: data.asset
          },
          allocation: idleAllocation,
          underlyingLiquidity: idleAllocation,
          isIdle: true,
          totalAssets
        });
        return result;
      }
    case 'Collateral':
      return orderBy(exposure.collaterals.map(collateral => ({
        item: {
          type: 'Token' as const,
          token: toCollateralAsset(chainId, collateral.token)
        },
        allocation: BigInt(collateral.allocatedAssets),
        absoluteCap: toCap(collateral.absoluteCap) ?? 0n,
        relativeCap: toCap(collateral.relativeCap) ?? 0n,
        totalAssets,
        pendingTimelocks: getCollateralCapPendingTimelocks(pendingTimelocks, collateral.token.address),
        caps,
        apy: getWeightedMarketsApy(marketRows.filter(({
          market
        }) => collateral.marketIds.includes(market.market.marketId)))
      })), row => row.allocation, 'desc');
    case 'Adapters':
      {
        const rows = orderBy(exposure.adapters.map(adapter => {
          const adapterMarkets = marketRows.filter(({
            market
          }) => market.adapterAddress.toLowerCase() === adapter.address.toLowerCase());
          const isVaultV1 = adapter.kind === VAULT_V1_KIND;
          const underlyingLiquidity = isVaultV1 ? toBigInt(adapter.underlyingLiquidity ?? undefined) ?? 0n : isMarketAdapter(adapter) ? adapterMarkets.reduce((sum, {
            market
          }) => sum + BigInt(market.liquidityAssets), 0n) : undefined;
          return {
            item: {
              type: 'Adapter' as const,
              name: adapterCleanName(adapter.kind)
            },
            allocation: BigInt(adapter.assets),
            underlyingLiquidity,
            absoluteCap: toCap(adapter.absoluteCap),
            relativeCap: toCap(adapter.relativeCap),
            totalAssets,
            isLiquidityAdapter: adapter.isLiquidityAdapter,
            pendingTimelocks: getAdapterCapPendingTimelocks(pendingTimelocks, adapter.address),
            caps,
            apy: isVaultV1 && adapter.vaultV1 ? getMetaMorphoVaultApy({
              chainId,
              vault: adapter.vaultV1,
              showPoints
            }) : isMarketAdapter(adapter) ? getWeightedMarketsApy(adapterMarkets) : undefined
          };
        }), row => row.allocation, 'desc');
        return [...rows, {
          item: {
            type: 'Adapter' as const,
            name: 'Idle Liquidity'
          },
          allocation: idleAllocation,
          underlyingLiquidity: idleAllocation,
          isIdle: true,
          totalAssets
        }];
      }
    default:
      return [];
  }
}